export const DOLLAR_VALUE_ROUNDING = {
    NO_ROUNDING: 'noRounding',
    ROUND_TO_NEAREST_DOLLAR: 'roundToNearestDollar'
} as const;

export type DollarValueRounding = (typeof DOLLAR_VALUE_ROUNDING)[keyof typeof DOLLAR_VALUE_ROUNDING];

export const DOLLAR_VALUE_ROUNDING_OPTIONS = [
    { label: 'Rounding to the nearest dollar', value: DOLLAR_VALUE_ROUNDING.ROUND_TO_NEAREST_DOLLAR },
    { label: 'No rounding', value: DOLLAR_VALUE_ROUNDING.NO_ROUNDING }
];

export const FONT_SIZE_ADJUSTMENT = {
    MINUS_2: '-2.0',
    MINUS_1_5: '-1.5',
    MINUS_1: '-1.0',
    MINUS_0_5: '-0.5',
    DEFAULT: '0',
    PLUS_0_5: '+0.5',
    PLUS_1: '+1.0',
    PLUS_1_5: '+1.5',
    PLUS_2: '+2.0'
} as const;

export type FontSizeAdjustment = (typeof FONT_SIZE_ADJUSTMENT)[keyof typeof FONT_SIZE_ADJUSTMENT];

export const FONT_SIZE_ADJUSTMENT_OPTIONS = [
    { label: '-2.0 points', value: FONT_SIZE_ADJUSTMENT.MINUS_2 },
    { label: '-1.5 points', value: FONT_SIZE_ADJUSTMENT.MINUS_1_5 },
    { label: '-1.0 points', value: FONT_SIZE_ADJUSTMENT.MINUS_1 },
    { label: '-0.5 points', value: FONT_SIZE_ADJUSTMENT.MINUS_0_5 },
    { label: 'Default (current font size)', value: FONT_SIZE_ADJUSTMENT.DEFAULT },
    { label: '+0.5 points', value: FONT_SIZE_ADJUSTMENT.PLUS_0_5 },
    { label: '+1.0 points', value: FONT_SIZE_ADJUSTMENT.PLUS_1 },
    { label: '+1.5 points', value: FONT_SIZE_ADJUSTMENT.PLUS_1_5 },
    { label: '+2.0 points', value: FONT_SIZE_ADJUSTMENT.PLUS_2 }
];

export type FieldConfigurationKey = string;

export type DisplayItemKey = string;

export type DisplayConfigState = Record<string, { active: boolean; displayName?: string; value?: string }>;

export const TEXT_INPUT_MAX_LENGTH = 1000;
export const MAX_DISCLAIMER_LENGTH = 550;

export const CARS_ACT_SPANISH_DISCLOSURE_LABEL = 'Print CARS Act Disclosure in Spanish';

// Descriptive labels for specific fieldConfiguration calculation/derived fields whose displayName values were shortened.
// These preserve user-friendly long forms in the UI "Field" column while inputs retain their shortened displayName.
export const GENERAL_CONFIGURATION_LABELS: Record<string, string> = {
    msrp: 'MSRP',
    retailPrice: 'Retail Price',
    discount: 'Discount',
    accessories: 'Accessories',
    netTrade: 'Net Trade',
    tradeAllowance: 'Trade Allowance',
    tradePayoff: 'Trade Payoff',
    priorBalance: 'Total Remaining Payments Amount',
    governmentFee: 'Government Fee(s)',
    procDocFee: 'Dealer Fee(s)',
    totalTaxes: 'Total Taxes',
    vehicleProtection: 'Vehicle Protection',
    cashDown: 'Cash Down',
    balanceDue: 'Balance Due',
    amountFinanced: 'Est. Total Financed',
    totalAmountDue: 'Est. Total Purchase Amount',
    totalDueAtSigning: 'Total Due at Signing',
    adjustedCapCost: 'Adjusted Cap Cost',
    residualValue: 'Residual Value',
    sellingPrice: 'Selling Price',
    totalSavings: 'Total Savings',
    subtotal: 'Subtotal',
    paidByCustomer: 'Paid by Customer',
    sellingPricePlusAddonsMinusRebateMinusCashDown: 'Selling Price + Add-ons - Rebates - Cash Down',
    sellingPricePlusAddons: 'Selling Price + Add-ons',
    sellingPricePlusAddonsMinusRebate: 'Selling Price + Add-ons - Rebates',
    sellingPriceMinusRebateMinusCashDown: 'Selling Price - Rebates - Cash Down',
    amountFinancedPlusCashDown: 'Amount Financed + Cash Down',
    sellingPricePlusAddonsPlusTotalTaxesMinusNetTradeMinusCashDownMinusRebate:
        'Selling Price + Add-ons + Total Taxes - Net Trade - Cash Down - Rebates',
    sellingPriceMinusNetTradeMinusRebateMinusCashDown: 'Selling Price - Net Trade - Rebates - Cash Down',
    sellingPriceMinusRebate: 'Selling Price - Rebates',
    sellingPricePlusAccessoriesMinusTradeAllowance: 'Selling Price + Accessories - Trade Allowance',
    sellingPricePlusAccessoriesPlusServiceContractsPlusGapMinusRebates:
        'Selling Price + Accessories + Service Contracts + GAP - Rebates',
    sellingPricePlusAccessoriesPlusServiceContractsPlusGapMinusRebatesMinusTradeAllowance:
        'Selling Price + Accessories + Service Contracts + GAP - Rebates - Trade Allowance',
    sellingPricePlusAddonsPlusTaxesMinusRebatesMinusTradeAllowance: 'Selling Price + Add-ons + Taxes - Rebates - Trade Allowance',
    sellingPricePlusAddonsMinusRebatesMinusTradeAllowance: 'Selling Price + Add-ons - Rebates - Trade Allowance',
    monthlyPaymentTotalPlusDownPayment: 'Monthly Payment Total + Down Payment',
    tradeTaxSavingsPlusAllowance: 'Trade Tax Savings + Allowance',
    sellingPricePlusAccessories: 'Selling Price + Accessories',
    sellingPricePlusAddonsMinusNetTradeMinusRebate: 'Selling Price + Add-ons - Net Trade - Rebates',
    sellingPricePlusAddonsPlusTaxesMinusRebates: 'Selling Price + Add-ons + Taxes - Rebates',
    sellingPriceMinusRebateMinusTradeAllowance: 'Selling Price - Rebates - Trade Allowance',
    amountFinancedMinusCash: 'Amount Financed - Cash',
    sellingPricePlusAddonsPlusTotalTaxesMinusNetTradeMinusRebate: 'Selling Price + Add-ons + Total Taxes - Net Trade - Rebates',
    msrpPlusAccessories: 'MSRP/Retail Price + Accessories',
    addons: 'Add-ons',
    tradeTaxSavings: 'Trade Tax Savings',
    capitalizedTaxes: 'Capitalized Taxes',
    additionalCapItems: 'Additional Cap Items',
    acquisitionFee: 'Acquisition Fee(s)',
    securityDeposit: 'Security Deposit',
    grossCapCost: 'Gross Cap Cost',
    cashCapReduction: 'Cash Cap Reduction',
    rebates: 'Rebates',
    totalFees: 'Total Fee(s)',
    taxesAndFees: 'Taxes & Fee(s)',
    grossTaxes: 'Gross Taxes',
    totalSavingsPlusRebate: 'Total Savings + Rebates',
    totalOfPaymentsAndConsideration: 'Est. Total Amount You Will Pay'
};

// Human readable labels for displayConfiguration items.
export const DISPLAY_CONFIGURATION_LABELS: Record<string, string> = {
    loanLeaseTerm: 'Loan/Lease Term',
    leaseMileage: 'Lease Mileage',
    apr: 'APR',
    monthlyPayment: 'Monthly Payment',
    stockNumber: 'Stock Number',
    totalPayment: 'Total Payment',
    showVehicleImage: 'Show Vehicle Image',
    showZeroDollarDisclosureItems: 'Show Zero Dollar Disclosure Items',
    odometer: 'Odometer',
    vin: 'VIN',
    customerSignature: 'Customer Signature',
    managerSignature: 'Manager Signature',
    itemizedAccessories: 'Itemized Accessories',
    itemizedVehicleProtection: 'Itemized Vehicle Protection',
    itemizedWarrantyDetails: 'Itemized Warranty Details',
    itemizedGovernmentFees: 'Itemized Government Fee(s)',
    itemizedProcDocFees: 'Itemized Dealer Fee(s)',
    itemizedTaxes: 'Itemized Taxes',
    itemizedTradeIn: 'Itemized Trade In',
    itemizedTaxedAndFees: 'Itemized Taxes and Fee(s)',
    itemizedDueAtSigning: 'Itemized Due at Signing',
    itemizedRebates: 'Itemized Rebates',
    fullCustomerData: 'Full customer contact information',
    dollarValueRounding: 'Dollar Value Rounding',
    conditionalDiscount: 'Conditional Discount',
    customDisclaimer: 'Customized Disclaimer',
    fontSizeAdjustment: 'Text Size',
    paymentRange: 'Payment Range',
    tradeEvaluation: 'Trade Evaluation',
    fuelSavingsAnalysis: 'Fuel Savings Analysis',
    carsActSpanishDisclosure: CARS_ACT_SPANISH_DISCLOSURE_LABEL
};

export const PAYMENT_RANGE_DEFAULT_SPREAD = 10;

export const TOOLTIP_CONTENT: Record<string, string> = {
    totalSavings: 'Total Savings = Discount + Rebates'
};

// Fields always shown as checked and disabled at the top of the 4-square General tab
export const FOUR_SQUARE_ALWAYS_ACTIVE_FIELDS = {
    discount: 'Discount',
    rebates: 'Rebates'
};

// Labels for 4-square General tab fields (key order defines display order)
export const FOUR_SQUARE_GENERAL_LABELS = {
    msrp: 'MSRP',
    retailPrice: 'Retail Price',
    sellingPrice: 'Selling Price',
    tradeAllowance: 'Trade Allowance',
    cashDown: 'Cash Down',
    cashAmountPaid: 'Cash Amount Paid',
    totalAmountDue: 'Est. Total Purchase Amount',
    monthlyPayment: 'Monthly Payment'
};

export type FourSquareFieldKey = keyof typeof FOUR_SQUARE_GENERAL_LABELS;
export const FOUR_SQUARE_FIELD_KEYS = Object.keys(FOUR_SQUARE_GENERAL_LABELS) as FourSquareFieldKey[];

// Labels for 4-square Display tab items (key order defines display order)
export const FOUR_SQUARE_DISPLAY_LABELS = {
    paymentRange: 'Payment Range',
    apr: 'APR',
    mf: 'Money Factor',
    loanLeaseTerm: 'Loan/Lease Term',
    odometer: 'Odometer / Mileage',
    vin: 'VIN',
    tradeIns: 'Trade-Ins',
    customerSignature: 'Customer Signature',
    managerSignature: 'Manager Signature',
    stockNumber: 'Stock Number',
    fullCustomerData: 'Full Customer Data',
    dollarValueRounding: 'Dollar Value Rounding',
    conditionalDiscount: 'Conditional Discount',
    carsActSpanishDisclosure: CARS_ACT_SPANISH_DISCLOSURE_LABEL
};

export type FourSquareDisplayKey = keyof typeof FOUR_SQUARE_DISPLAY_LABELS;
